import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Download, Mail, Loader2, Lock, ArrowRight, CheckCircle2, CalendarClock, Share2, Linkedin, Copy, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CountUp from "@/components/CountUp";
import LevelBadge from "@/components/LevelBadge";
import { api, API } from "@/lib/api";
import { shortLabelFor, scoreColor, roundScore, levelColor } from "@/lib/categoryChip";

export default function Report() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const revealed = params.get("revealed") === "1";
  const [report, setReport] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [config, setConfig] = useState({ book_call_url: "", book_call_label: "Book a strategy call", public_app_url: "" });
  const [loading, setLoading] = useState(true);
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [company, setCompany] = useState("");
  const [submittingLead, setSubmittingLead] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, c, b] = await Promise.all([
          api.get(`/report/${id}`),
          api.get(`/config`).catch(() => ({ data: {} })),
          api.get(`/report/${id}/benchmark`).catch(() => ({ data: null })),
        ]);
        setReport(r.data);
        setConfig({ ...config, ...(c.data || {}) });
        setBenchmark(b.data);
      } catch (e) {
        toast.error("Report not found.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter your email.");
      return;
    }
    setSubmittingLead(true);
    try {
      const res = await api.post("/lead", {
        submission_id: id,
        email,
        first_name: firstName || null,
        company: company || null,
      });
      setEmailUnlocked(true);
      if (res.data.email_sent) toast.success("Report sent to your email.");
      else toast.success("Report unlocked. Download below.");
    } catch (e) {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmittingLead(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C6F84C]" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Not found</div>
            <h1 className="mt-3 text-3xl font-bold">This report doesn't exist.</h1>
            <Link to="/quiz" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#C6F84C] text-black font-bold px-6 py-3">
              Take the audit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const radarData = Object.keys(report.categories).map((key) => ({
    category: shortLabel(report.category_labels[key]),
    score: report.categories[key],
    benchmark: 70,
  }));

  const pdfUrl = `${API}/report/${id}/pdf`;
  const strongest = Object.entries(report.categories).sort((a, b) => b[1] - a[1])[0];
  const weakest = Object.entries(report.categories).sort((a, b) => a[1] - b[1])[0];

  return (
    <div className="grain min-h-screen">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Score hero */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1 rounded-2xl border border-white/10 bg-[#121212] p-8 lime-glow"
          >
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Your QR Readiness Score</div>
            <div className="mt-4 flex items-baseline gap-2">
              {revealed ? (
                <CountUp
                  value={report.overall}
                  className="font-mono text-8xl font-black lime-text-glow leading-none tabular-nums"
                  testId="report-score-value"
                  style={{ color: levelColor(report.level) }}
                />
              ) : (
                <span
                  className="font-mono text-8xl font-black lime-text-glow leading-none tabular-nums"
                  style={{ color: levelColor(report.level) }}
                  data-testid="report-score-value"
                >
                  {report.overall}
                </span>
              )}
              <span className="text-zinc-500 text-2xl">/100</span>
            </div>
            <div className="mt-4">
              <LevelBadge level={report.level} />
            </div>
            <p className="mt-6 text-zinc-400 leading-relaxed">{report.interpretation}</p>
            <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Strongest</div>
                <div className="mt-1 text-[#C6F84C] font-medium">{report.category_labels[strongest[0]]}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Weakest</div>
                <div className="mt-1 text-red-400 font-medium">{report.category_labels[weakest[0]]}</div>
              </div>
            </div>
          </motion.div>

          {/* Radar */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.5 }}
            className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#121212] p-8"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Category breakdown</div>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Six categories vs. a 70-point benchmark</h2>
              </div>
            </div>
            <div className="mt-4 h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke="#27272A" />
                  <PolarAngleAxis dataKey="category" stroke="#A1A1AA" tick={{ fill: "#A1A1AA", fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="#27272A" tick={{ fill: "#71717A", fontSize: 10 }} />
                  <Radar name="Benchmark" dataKey="benchmark" stroke="#52525B" fill="#52525B" fillOpacity={0.1} />
                  <Radar name="You" dataKey="score" stroke="#C6F84C" fill="#C6F84C" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </section>

        {/* Benchmark widget */}
        {benchmark && <BenchmarkSection benchmark={benchmark} categoryLabels={report.category_labels} />}

        {/* Category table */}
        <section className="mt-8 rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Category detail</div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">Where the score comes from</h2>
          </div>
          <div className="divide-y divide-white/5">
            {Object.entries(report.categories).map(([key, score]) => (
              <div key={key} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`report-category-${key}`}>
                <div className="sm:w-56 font-medium">{report.category_labels[key]}</div>
                <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden max-w-md">
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${Math.max(2, score)}%`, background: scoreColor(score) }}
                  />
                </div>
                <div className="font-mono font-bold w-14 text-right" style={{ color: scoreColor(score) }}>
                  {roundScore(score)}
                </div>
                <div className="sm:flex-1 text-sm text-zinc-500">{diagnosisFor(key, score, report.category_labels, key === weakest[0])}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Analysis */}
        <section className="mt-8 rounded-2xl border border-white/10 bg-[#121212] p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Analysis</div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight">What's driving your score</h2>
          <div className="mt-4 space-y-4">
            {report.analysis.map((p, i) => (
              <p key={i} className="text-zinc-400 leading-relaxed">{p}</p>
            ))}
          </div>
        </section>

        {/* Quick wins — gated */}
        <section className="mt-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Quick wins</div>
              <h2 className="mt-2 text-2xl font-bold tracking-tight">Three moves, ranked by impact</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {report.quick_wins.map((qw, i) => {
              const blurred = !emailUnlocked && i > 0;
              return (
                <div
                  key={qw.id}
                  className={`relative rounded-2xl border border-white/10 bg-[#121212] p-6 ${blurred ? "select-none" : ""}`}
                  data-testid={`quick-win-${i}`}
                >
                  <div className={`${blurred ? "blur-sm pointer-events-none" : ""}`}>
                    <div className="flex items-baseline justify-between">
                      <div className="font-mono text-xs text-[#C6F84C] font-bold">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500">
                        {impactLabel(qw.impact)} · {qw.effort_label}
                      </div>
                    </div>
                    <h3 className="mt-3 font-display text-lg font-bold leading-tight">{qw.title}</h3>
                    <p className="mt-2 text-sm text-zinc-400">{qw.why}</p>
                    <ul className="mt-4 space-y-2">
                      {qw.steps.map((s, si) => (
                        <li key={si} className="flex items-start gap-2 text-sm text-zinc-300">
                          <CheckCircle2 className="w-4 h-4 text-[#C6F84C] flex-shrink-0 mt-0.5" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {blurred && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-black/40">
                      <Lock className="w-5 h-5 text-[#C6F84C]" />
                      <div className="text-xs uppercase tracking-widest text-zinc-400">Unlock full report</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Email gate / download */}
        <section className="mt-10 rounded-2xl border border-[#C6F84C]/40 bg-[#121212] p-8 lime-glow">
          {!emailUnlocked ? (
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">Get the full PDF</div>
                <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">
                  4-page PDF, sent to your inbox.
                </h2>
                <p className="mt-3 text-zinc-400">
                  Includes your score, category breakdown, driving-factor analysis, and all three
                  quick wins with implementation steps.
                </p>
              </div>
              <form onSubmit={handleUnlock} className="space-y-3" data-testid="lead-form">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="First name (optional)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    data-testid="lead-first-name-input"
                    className="bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#C6F84C] transition-colors duration-200"
                  />
                  <input
                    type="text"
                    placeholder="Company (optional)"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    data-testid="lead-company-input"
                    className="bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#C6F84C] transition-colors duration-200"
                  />
                </div>
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="lead-email-input"
                  className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#C6F84C] transition-colors duration-200"
                />
                <button
                  type="submit"
                  disabled={submittingLead}
                  data-testid="lead-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] disabled:opacity-50 text-black font-bold px-6 py-3.5 transition-colors duration-200"
                >
                  {submittingLead ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" /> Get my full report
                    </>
                  )}
                </button>
                <p className="text-xs text-zinc-500">No spam. One email with your PDF report.</p>
              </form>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">Report unlocked</div>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Your PDF is ready.</h2>
                <p className="mt-2 text-zinc-400">Download it now or check your inbox.</p>
              </div>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pdf-download-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-6 py-3.5 transition-colors duration-200"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            </div>
          )}
        </section>

        {/* Book-a-Call CTA */}
        {config.book_call_url && (
          <section className="mt-8 rounded-2xl border border-white/10 bg-[#121212] p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Next step (optional)</div>
              <h2 className="mt-2 text-2xl font-bold tracking-tight">
                Want a human to walk you through the report?
              </h2>
              <p className="mt-2 text-zinc-400 max-w-xl">
                A 20-minute call with our team to prioritise the fixes above against your actual campaigns, surfaces, and budget.
              </p>
            </div>
            <a
              href={config.book_call_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#C6F84C] hover:bg-[#C6F84C] hover:text-black text-[#C6F84C] font-bold px-6 py-3.5 transition-colors duration-200"
            >
              <CalendarClock className="w-4 h-4" /> {config.book_call_label}
            </a>
          </section>
        )}

        {/* Shareable badge — Best-in-Class only */}
        {report.level === "Best-in-Class" && <ShareBadgeSection id={id} report={report} config={config} />}
      </main>

      <Footer />
    </div>
  );
}

function impactLabel(n) {
  return { 3: "High impact", 2: "Medium impact", 1: "Low impact" }[n] || "Medium impact";
}

function shortLabel(s) {
  return s?.replace("Call-to-Action & Value", "CTA & Value").replace("Mobile UX (Landing Page)", "Mobile UX").replace(" & ", " / ");
}

function diagnosisFor(key, score, labels, isWeakest = false) {
  if (score >= 80) return `${labels[key]} is a strength — refine, don't rebuild.`;
  if (score >= 60) return `${labels[key]} is solid with clear room to sharpen.`;
  if (score >= 40) return `Meaningful gaps costing conversions.`;
  if (isWeakest) return `Biggest blocker — fix first.`;
  return `Underperforming — address after the top blocker.`;
}

function BenchmarkSection({ benchmark, categoryLabels }) {
  // Not enough data yet
  if (!benchmark || benchmark.beats_pct == null) {
    return (
      <section className="mt-8 rounded-2xl border border-white/10 bg-[#121212] p-8" data-testid="benchmark-empty">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-zinc-500" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Industry benchmark</div>
            <h2 className="mt-1 text-lg font-bold tracking-tight">Not enough peer data yet.</h2>
          </div>
        </div>
        <p className="mt-3 text-zinc-400 text-sm max-w-lg">
          {benchmark?.note || "This section will appear once more audits have been taken."}
        </p>
      </section>
    );
  }

  const beats = benchmark.beats_pct;
  const rank = benchmark.percentile; // top X%
  return (
    <section className="mt-8 rounded-2xl border border-white/10 bg-[#121212] p-8" data-testid="benchmark-section">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
            <TrendingUp className="w-4 h-4 text-[#C6F84C]" /> Industry benchmark
          </div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight">
            You beat <span className="text-[#C6F84C]">{beats}%</span> of {benchmark.peers.toLocaleString()} audited businesses.
          </h2>
          <p className="mt-2 text-zinc-400 max-w-xl">
            {rank <= 10
              ? "That's in the top 10% — a real differentiator you can lean on with prospects and partners."
              : rank <= 25
              ? "You're in the top 25% — clearly above the average operator."
              : rank <= 50
              ? "Roughly middle of the pack. The quick wins below are where the biggest movers spent their next week."
              : "Below the median. The good news: the median is not high, and the quick wins below reliably move it fast."}
          </p>
          <div className="mt-3 text-xs text-zinc-500">
            Anonymised aggregate · your score {benchmark.overall} vs peer average {benchmark.overall_avg}
          </div>
        </div>

        {/* Category delta chips */}
        {benchmark.category_deltas && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full lg:w-auto">
            {Object.entries(benchmark.category_deltas).map(([key, delta]) => {
              const positive = delta >= 0;
              return (
                <div
                  key={key}
                  className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 min-w-[130px]"
                  data-testid={`benchmark-delta-${key}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {(categoryLabels[key] || key).replace(" (Landing Page)", "")}
                  </div>
                  <div className={`mt-1 font-mono font-bold text-sm ${positive ? "text-[#C6F84C]" : "text-red-400"}`}>
                    {positive ? "+" : ""}{delta} vs avg
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function ShareBadgeSection({ id, report, config }) {
  const base = config.public_app_url || (typeof window !== "undefined" ? window.location.origin : "");
  const badgeUrl = `${base}/badge/${id}`;
  // Use the /api/share/badge/:id endpoint for LinkedIn — it returns HTML with a
  // per-badge og:image so LinkedIn renders a real score card preview.
  const shareUrl = `${base}/api/share/badge/${id}`;

  const linkedInText = `I just scored ${report.overall}/100 — Best-in-Class — on the QR Readiness Audit. See the rubric and take yours: ${shareUrl}`;
  const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(badgeUrl);
      toast.success("Badge link copied.");
    } catch (e) {
      toast.error("Couldn't copy — long-press the link instead.");
    }
  };

  return (
    <section className="mt-8 rounded-2xl border border-emerald-400/40 bg-[#0F1512] p-8" data-testid="share-badge-section">
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-bold">Best-in-class</div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">Share your badge.</h2>
          <p className="mt-3 text-zinc-400 max-w-lg">
            You're in the top tier. Get credit for it — share a public badge page with your network on LinkedIn or via a direct link.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={linkedInHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-linkedin-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-5 py-3 transition-colors duration-200"
              onClick={() => {
                // Prefill text via clipboard as a nudge
                try { navigator.clipboard?.writeText(linkedInText); } catch (e) { /* ignore */ }
              }}
            >
              <Linkedin className="w-4 h-4" /> Share on LinkedIn
            </a>
            <button
              type="button"
              onClick={copyLink}
              data-testid="share-copy-link-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white font-medium px-5 py-3 transition-colors duration-200"
            >
              <Copy className="w-4 h-4" /> Copy link
            </button>
            <Link
              to={`/badge/${id}`}
              data-testid="view-badge-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white font-medium px-5 py-3 transition-colors duration-200"
            >
              <Share2 className="w-4 h-4" /> View public badge
            </Link>
          </div>
        </div>

        {/* Live badge preview */}
        <div className="relative rounded-2xl border border-emerald-400/30 bg-black p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-bold">QR Readiness · Best-in-Class</div>
          <div className="mt-4 flex items-baseline gap-2">
            <div className="font-mono text-7xl font-black text-emerald-400 leading-none tabular-nums">{report.overall}</div>
            <div className="text-zinc-500 text-lg">/100</div>
          </div>
          <div className="mt-4 text-sm text-zinc-400">Verified against the QR Readiness rubric.</div>
        </div>
      </div>
    </section>
  );
}
